import React, { useMemo, useRef, useState } from 'react';
import Sym from './Sym';

/**
 * Calendario de rango del sistema.
 *
 * Sustituye a los dos `<input type="date">` nativos: eran lo único de la app que
 * no hablaba su mismo idioma visual —cada navegador y cada sistema los pinta a
 * su manera— y obligaban a elegir las dos fechas por separado, sin ver el rango.
 *
 * Se escribe a mano y no con una librería porque el caso es acotado (elegir un
 * rango de días, sin horas ni zonas), el paquete más liviano ronda los 30 kB y
 * el bundle ya es grande; además así el calendario usa los tokens del tema y
 * sigue al modo claro/oscuro sin pelearse con estilos ajenos.
 *
 * Fechas SIEMPRE locales, en 'YYYY-MM-DD'. No se usa toISOString: convierte a
 * UTC y, según la zona del navegador, devuelve el día anterior.
 *
 * @param {string} start  'YYYY-MM-DD'
 * @param {string} end    'YYYY-MM-DD'
 * @param {Function} onChange  ({ startDate, endDate }) => void
 * @param {string} max    último día seleccionable (por defecto hoy)
 * @param {number} months meses visibles (2 en escritorio, 1 en móvil)
 */

const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

export const isoLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const deIso = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const mismoDia = (a, b) => a && b && isoLocal(a) === isoLocal(b);

const nombreMes = (fecha) => {
  // Solo la primera letra: `text-transform: capitalize` también ponía en
  // mayúscula el "de" ("Junio De 2026").
  const t = fecha.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const etiquetaLarga = (fecha) => fecha.toLocaleDateString('es-MX', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
});

/** Cuadrícula del mes: 6 semanas de 7 días, con lunes como primer día. */
const semanasDe = (año, mes) => {
  const primero = new Date(año, mes, 1);
  // getDay(): 0 = domingo. Se desplaza para que la semana empiece en lunes.
  const desplazamiento = (primero.getDay() + 6) % 7;
  const inicio = new Date(año, mes, 1 - desplazamiento);

  const semanas = [];
  for (let s = 0; s < 6; s++) {
    const dias = [];
    for (let d = 0; d < 7; d++) {
      dias.push(new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + s * 7 + d));
    }
    semanas.push(dias);
  }
  return semanas;
};

const RangeCalendar = ({ start, end, onChange, max, months = 2 }) => {
  const hoy = useMemo(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }, []);
  const tope = useMemo(() => deIso(max) || hoy, [max, hoy]);

  const inicio = deIso(start);
  const fin = deIso(end);

  const [mesBase, setMesBase] = useState(() => {
    const ref = inicio || hoy;
    // Con dos meses a la vista, el mes de la fecha elegida queda a la derecha:
    // así se ve también el mes anterior, que es lo que se suele buscar.
    return new Date(ref.getFullYear(), ref.getMonth() - (months > 1 ? 1 : 0), 1);
  });
  const [hover, setHover] = useState(null);
  const [foco, setFoco] = useState(() => inicio || hoy);
  const rejilla = useRef(null);

  const mesesVisibles = Array.from({ length: months }, (_, i) => (
    new Date(mesBase.getFullYear(), mesBase.getMonth() + i, 1)
  ));

  // Mientras se elige el fin, el rango se pinta siguiendo el cursor.
  const finVisual = fin || (inicio && hover && hover > inicio ? hover : null);

  const dentroDelRango = (dia) => {
    if (!inicio || !finVisual) return false;
    return dia > inicio && dia < finVisual;
  };

  const elegir = (dia) => {
    if (dia > tope) return;

    // Sin inicio, o con el rango ya completo: empieza uno nuevo.
    if (!inicio || (inicio && fin)) {
      onChange({ startDate: isoLocal(dia), endDate: '' });
      return;
    }
    // Con inicio y sin fin: hacia atrás mueve el inicio; hacia adelante cierra.
    if (dia < inicio) {
      onChange({ startDate: isoLocal(dia), endDate: '' });
      return;
    }
    onChange({ startDate: isoLocal(inicio), endDate: isoLocal(dia) });
  };

  /** Flechas para moverse por el calendario sin ratón. */
  const enTecla = (e, dia) => {
    const saltos = {
      ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7,
    };
    if (saltos[e.key] !== undefined) {
      e.preventDefault();
      const destino = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate() + saltos[e.key]);
      setFoco(destino);
      // Si el salto se sale de los meses a la vista, se avanza la vista.
      const primerVisible = mesBase;
      const ultimoVisible = new Date(mesBase.getFullYear(), mesBase.getMonth() + months, 0);
      if (destino < primerVisible) setMesBase(new Date(destino.getFullYear(), destino.getMonth(), 1));
      if (destino > ultimoVisible) {
        setMesBase(new Date(destino.getFullYear(), destino.getMonth() - months + 1, 1));
      }
      // El foco real se coloca tras el repintado.
      setTimeout(() => {
        rejilla.current?.querySelector(`[data-dia="${isoLocal(destino)}"]`)?.focus();
      }, 0);
      return;
    }
    if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      setMesBase((m) => new Date(m.getFullYear(), m.getMonth() + (e.key === 'PageUp' ? -1 : 1), 1));
    }
  };

  const renderMes = (fecha) => (
    <div key={isoLocal(fecha)} style={{ minWidth: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {DIAS.map((d, i) => (
          <div
            key={`${d}-${i}`}
            aria-hidden
            style={{
              textAlign: 'center', font: "700 .64rem 'IBM Plex Mono'", color: 'var(--faint)',
              padding: '4px 0',
            }}
          >
            {d}
          </div>
        ))}

        {semanasDe(fecha.getFullYear(), fecha.getMonth()).flat().map((dia) => {
          const delMes = dia.getMonth() === fecha.getMonth();
          const futuro = dia > tope;
          const esInicio = mismoDia(dia, inicio);
          const esFin = mismoDia(dia, finVisual);
          const enRango = dentroDelRango(dia);
          const esHoy = mismoDia(dia, hoy);
          const extremo = esInicio || esFin;

          return (
            <button
              key={isoLocal(dia)}
              type="button"
              className="a2-cal-dia"
              data-dia={isoLocal(dia)}
              disabled={futuro}
              tabIndex={mismoDia(dia, foco) ? 0 : -1}
              aria-label={etiquetaLarga(dia)}
              aria-pressed={extremo || enRango}
              aria-current={esHoy ? 'date' : undefined}
              onClick={() => elegir(dia)}
              onMouseEnter={() => setHover(dia)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setFoco(dia)}
              onKeyDown={(e) => enTecla(e, dia)}
              style={{
                borderRadius: extremo ? 9 : 7, border: 'none', cursor: futuro ? 'not-allowed' : 'pointer',
                font: `${extremo ? 700 : 500} .82rem 'Hanken Grotesk'`,
                background: extremo ? 'var(--brand)' : enRango ? 'var(--brand-soft)' : 'transparent',
                color: extremo ? '#fff'
                  : futuro ? 'var(--faint)'
                    : delMes ? 'var(--heading)' : 'var(--faint)',
                opacity: futuro ? 0.45 : delMes ? 1 : 0.5,
                outline: esHoy && !extremo ? '1px solid var(--brand-line)' : 'none',
                outlineOffset: -1,
                transition: 'background .12s',
              }}
            >
              {dia.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      {/* Cabecera: mes anterior · nombres de los meses · mes siguiente */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <button
          type="button"
          aria-label="Mes anterior"
          onClick={() => setMesBase((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
          className="a2-btn a2-btn--icon"
          style={{ width: 28, height: 28, flexShrink: 0 }}
        >
          <Sym name="chevron_left" size={17} />
        </button>

        <div
          style={{
            flex: 1, display: 'grid', gridTemplateColumns: `repeat(${months},1fr)`, gap: 16,
          }}
        >
          {mesesVisibles.map((m) => (
            <div
              key={`t-${isoLocal(m)}`}
              style={{
                textAlign: 'center', font: "700 .84rem 'Hanken Grotesk'", color: 'var(--heading)',
              }}
            >
              {nombreMes(m)}
            </div>
          ))}
        </div>

        <button
          type="button"
          aria-label="Mes siguiente"
          onClick={() => setMesBase((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
          className="a2-btn a2-btn--icon"
          style={{ width: 28, height: 28, flexShrink: 0 }}
        >
          <Sym name="chevron_right" size={17} />
        </button>
      </div>

      <div
        ref={rejilla}
        role="group"
        aria-label="Elegir el periodo"
        style={{ display: 'grid', gridTemplateColumns: `repeat(${months},1fr)`, gap: 16 }}
      >
        {mesesVisibles.map(renderMes)}
      </div>

      <p
        style={{
          font: "500 .74rem 'Hanken Grotesk'", color: 'var(--muted)',
          margin: '10px 0 0', textAlign: 'center',
        }}
      >
        {inicio && !fin
          ? 'Ahora elige el último día del periodo'
          : 'Toca el primer día y luego el último'}
      </p>
    </div>
  );
};

export default RangeCalendar;
